// file: src/modules/administration/authentication/authentication.jsx
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Button, Form, Tooltip } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { Body } from "../../../components/main-body/body";
import editIcon from "../../../assets/edit.svg";
import { useTranslation } from "react-i18next";
import useNotification from "../../../components/notification/notification";
import { FormInput, FormSwitch } from "../../../components/form/form";
import classNames from "classnames";

import ApiSystemSettingAuth from "../../../api/system-setting-auth";
import useAdminEvents from "../../../hooks/useAdminEvents";

import "./authentication.css";

async function copyToClipboard(text) {
  if (!text) return false;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) {}

  try {
    const el = document.createElement("textarea");
    el.value = text;
    el.setAttribute("readonly", "");
    el.style.position = "absolute";
    el.style.left = "-9999px";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(el);
    return ok;
  } catch (_) {
    return false;
  }
}

// Маска у форматі ****Q8o1
function isMaskedSecret(v) {
  if (!v) return false;
  const s = String(v).trim();
  return /^(\*{4,}).{4}$/.test(s); // **** + 4 останні
}

const Authentication = () => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();

  const notifyRef = useRef(openNotification);
  useEffect(() => {
    notifyRef.current = openNotification;
  }, [openNotification]);

  const [form] = Form.useForm();

  const [isEdit, setIsEdit] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(false);

  const [data, setData] = useState(null);
  const [initialData, setInitialData] = useState(null);

  // ✅ секрети тримаємо ОКРЕМО і ніколи не кладемо в data
  const [googleSecret, setGoogleSecret] = useState("");
  const [microsoftSecret, setMicrosoftSecret] = useState("");

  const googleEnabled = !!data?.portalAuthGoogleEnabled;
  const microsoftEnabled = !!data?.portalAuthMicrosoftEnabled;

  const toggleEdit = useCallback(() => {
    setIsEdit((v) => !v);
    setHasChanges(false);
  }, []);

  const cancel = useCallback(() => {
    setIsEdit(false);
    setHasChanges(false);

    setData(initialData);

    // очистити введені секрети
    setGoogleSecret("");
    setMicrosoftSecret("");

    // відновити поля форми
    form.setFieldsValue({ ...(initialData || {}) });
  }, [form, initialData]);

  const onChange = useCallback(
    (key, v) => {
      if (!hasChanges) setHasChanges(true);

      let value;
      if (typeof v === "boolean") value = v;
      else if (v?.target?.value !== undefined) value = v.target.value;
      else value = v;

      setData((prev) => ({ ...(prev || {}), [key]: value }));
      form.setFieldValue(key, value);
    },
    [hasChanges, form]
  );

  const fetchAuthSettings = useCallback(async () => {
    const res = await ApiSystemSettingAuth.get();
    const s = res?.data?.systemSetting || null;

    setData(s);
    setInitialData(s);

    // у формі тримаємо лише те, що реально редагується (без секретів)
    form.setFieldsValue({ ...(s || {}) });

    // ✅ секрети завжди пусті після fetch
    setGoogleSecret("");
    setMicrosoftSecret("");
  }, [form]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        await fetchAuthSettings();
      } catch (e) {
        if (!cancelled) {
          notifyRef.current?.({
            type: "error",
            message: <p>Failed to load authentication settings</p>,
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fetchAuthSettings]);

  useAdminEvents({
    onSettingChanged: (payload) => {
      if (payload?.group === "authentication") {
        fetchAuthSettings();
      }
    },
  });

  const update = useCallback(async () => {
    try {
      setLoading(true);

      // payload = ТІЛЬКИ редаговані поля + секрети, якщо введені
      const payload = {
        portalAuthGoogleEnabled: !!data?.portalAuthGoogleEnabled,
        portalAuthGoogleClientId:
          (data?.portalAuthGoogleClientId || "").trim() || null,

        portalAuthMicrosoftEnabled: !!data?.portalAuthMicrosoftEnabled,
        portalAuthMicrosoftClientId:
          (data?.portalAuthMicrosoftClientId || "").trim() || null,
      };

      // ✅ секрет додаємо лише якщо реально введений (і не маска)
      const g = String(googleSecret || "").trim();
      if (g && !isMaskedSecret(g)) payload.portalAuthGoogleClientSecret = g;

      const m = String(microsoftSecret || "").trim();
      if (m && !isMaskedSecret(m)) payload.portalAuthMicrosoftClientSecret = m;

      await ApiSystemSettingAuth.update(payload);

      notifyRef.current?.({
        type: "success",
        message: <p>Authentication settings updated</p>,
      });

      await fetchAuthSettings();

      setHasChanges(false);
      setIsEdit(false);
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        "Failed to update authentication settings";

      notifyRef.current?.({ type: "error", message: <p>{msg}</p> });
    } finally {
      setLoading(false);
    }
  }, [data, googleSecret, microsoftSecret, fetchAuthSettings]);

  // бек повертає ****LAST4 (наприклад ****Q8o1)
  const googleSecretMasked = useMemo(() => {
    const m = data?.portalAuthGoogleClientSecretMasked;
    return m ? String(m) : "";
  }, [data?.portalAuthGoogleClientSecretMasked]);

  const microsoftSecretMasked = useMemo(() => {
    const m = data?.portalAuthMicrosoftClientSecretMasked;
    return m ? String(m) : "";
  }, [data?.portalAuthMicrosoftClientSecretMasked]);

  const onCopy = useCallback(async (text) => {
    const ok = await copyToClipboard(text);
    notifyRef.current?.({
      type: ok ? "success" : "error",
      message: <p>{ok ? "Copied" : "Failed to copy"}</p>,
    });
  }, []);

  // секрети міняються окремо — теж фіксуємо hasChanges
  const onGoogleSecretChange = useCallback(
    (e) => {
      if (!hasChanges) setHasChanges(true);
      setGoogleSecret(e?.target?.value ?? "");
    },
    [hasChanges]
  );

  const onMicrosoftSecretChange = useCallback(
    (e) => {
      if (!hasChanges) setHasChanges(true);
      setMicrosoftSecret(e?.target?.value ?? "");
    },
    [hasChanges]
  );

  return (
    <Body scroll header={{ title: "Authentication" }}>
      <div className="auth-portal-settings">
        <div className="profile_form-container">
          <div
            className="profile_form__header authps-pad"
            style={{ marginBottom: 12 }}
          >
            <span className="profile_form__header__title">
              Portal Authentication
            </span>

            <div style={{ height: 40 }}>
              {isEdit ? (
                <div className="profile-header__btns">
                  <Button
                    style={{ height: 40, background: "rgba(108,108,137,0.08)" }}
                    onClick={cancel}
                    disabled={loading}
                  >
                    {t("profile.btns.cancel")}
                  </Button>

                  <Button
                    type="primary"
                    style={{ marginLeft: 8, height: 40 }}
                    onClick={update}
                    disabled={!hasChanges}
                    loading={loading}
                  >
                    {t("profile.btns.save")}
                  </Button>
                </div>
              ) : (
                <img
                  className="profile_form__header__edit-btn"
                  src={editIcon}
                  alt="edit"
                  role="presentation"
                  onClick={toggleEdit}
                />
              )}
            </div>
          </div>

          <Form form={form} layout="vertical">
            {/* ===== GOOGLE ===== */}
            <div className="authps-section authps-pad">
              <div className="authps-row">
                <div className="authps-label">Login with Google</div>
                <div className="authps-value">
                  <Form.Item name="portalAuthGoogleEnabled" style={{ margin: 0 }}>
                    <FormSwitch
                      checked={!!data?.portalAuthGoogleEnabled}
                      onChange={(v) => onChange("portalAuthGoogleEnabled", v)}
                      readOnly={!isEdit}
                      className={classNames("authps-switch", {
                        "authps-switch--edit": isEdit,
                      })}
                    />
                  </Form.Item>
                </div>
              </div>

              {googleEnabled ? (
                <div className="authps-fields">
                  <div className="authps-field">
                    <div className="authps-field__label">Client ID</div>
                    <Form.Item
                      name="portalAuthGoogleClientId"
                      style={{ margin: 0 }}
                    >
                      <FormInput
                        readOnly={!isEdit}
                        value={data?.portalAuthGoogleClientId || ""}
                        onChange={(v) => onChange("portalAuthGoogleClientId", v)}
                        className={classNames("authps-input", {
                          "authps-input--edit": isEdit,
                        })}
                      />
                    </Form.Item>
                  </div>

                  <div className="authps-field">
                    <div className="authps-field__label">Client Secret</div>

                    <Form.Item style={{ margin: 0 }}>
                      <FormInput
                        readOnly={!isEdit}
                        type="password"
                        value={googleSecret}
                        // ✅ показуємо маску В ІНПУТІ як placeholder (і в view, і в edit)
                        placeholder={googleSecretMasked || "••••••••"}
                        onChange={isEdit ? onGoogleSecretChange : undefined}
                        className={classNames("authps-input", {
                          "authps-input--edit": isEdit,
                        })}
                      />
                    </Form.Item>
                  </div>

                  <div className="authps-field">
                    <div className="authps-field__top">
                      <div className="authps-field__label">Redirect URL</div>

                      <Tooltip title="Copy">
                        <button
                          type="button"
                          className="authps-copy-icon"
                          onClick={() => onCopy(data?.portalAuthGoogleRedirectUrl)}
                          disabled={!data?.portalAuthGoogleRedirectUrl}
                          aria-label="Copy redirect url"
                        >
                          <CopyOutlined />
                        </button>
                      </Tooltip>
                    </div>

                    <Form.Item
                      name="portalAuthGoogleRedirectUrl"
                      style={{ margin: 0 }}
                    >
                      <FormInput
                        disabled={true}
                        readOnly={true}
                        value={data?.portalAuthGoogleRedirectUrl || ""}
                        className="authps-input authps-input--disabled"
                      />
                    </Form.Item>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="authps-divider" />

            {/* ===== MICROSOFT ===== */}
            <div className="authps-section authps-pad">
              <div className="authps-row">
                <div className="authps-label">Login with Microsoft</div>
                <div className="authps-value">
                  <Form.Item
                    name="portalAuthMicrosoftEnabled"
                    style={{ margin: 0 }}
                  >
                    <FormSwitch
                      checked={!!data?.portalAuthMicrosoftEnabled}
                      onChange={(v) =>
                        onChange("portalAuthMicrosoftEnabled", v)
                      }
                      readOnly={!isEdit}
                      className={classNames("authps-switch", {
                        "authps-switch--edit": isEdit,
                      })}
                    />
                  </Form.Item>
                </div>
              </div>

              {microsoftEnabled ? (
                <div className="authps-fields">
                  <div className="authps-field">
                    <div className="authps-field__label">Client ID</div>
                    <Form.Item
                      name="portalAuthMicrosoftClientId"
                      style={{ margin: 0 }}
                    >
                      <FormInput
                        readOnly={!isEdit}
                        value={data?.portalAuthMicrosoftClientId || ""}
                        onChange={(v) =>
                          onChange("portalAuthMicrosoftClientId", v)
                        }
                        className={classNames("authps-input", {
                          "authps-input--edit": isEdit,
                        })}
                      />
                    </Form.Item>
                  </div>

                  <div className="authps-field">
                    <div className="authps-field__label">Client Secret</div>

                    <Form.Item style={{ margin: 0 }}>
                      <FormInput
                        readOnly={!isEdit}
                        type="password"
                        value={microsoftSecret}
                        // ✅ маска В ІНПУТІ як placeholder (і в view, і в edit)
                        placeholder={microsoftSecretMasked || "••••••••"}
                        onChange={isEdit ? onMicrosoftSecretChange : undefined}
                        className={classNames("authps-input", {
                          "authps-input--edit": isEdit,
                        })}
                      />
                    </Form.Item>
                  </div>

                  <div className="authps-field">
                    <div className="authps-field__top">
                      <div className="authps-field__label">Redirect URL</div>

                      <Tooltip title="Copy">
                        <button
                          type="button"
                          className="authps-copy-icon"
                          onClick={() =>
                            onCopy(data?.portalAuthMicrosoftRedirectUrl)
                          }
                          disabled={!data?.portalAuthMicrosoftRedirectUrl}
                          aria-label="Copy redirect url"
                        >
                          <CopyOutlined />
                        </button>
                      </Tooltip>
                    </div>

                    <Form.Item
                      name="portalAuthMicrosoftRedirectUrl"
                      style={{ margin: 0 }}
                    >
                      <FormInput
                        disabled={true}
                        readOnly={true}
                        value={data?.portalAuthMicrosoftRedirectUrl || ""}
                        className="authps-input authps-input--disabled"
                      />
                    </Form.Item>
                  </div>
                </div>
              ) : null}
            </div>
          </Form>
        </div>
      </div>
    </Body>
  );
};

export default Authentication;
